<template>
    <div class="g-ops">
        <template v-if="loading">
            <loader class="g-ops-loader"/>
        </template>

        <div
            class="col-sm-7 col-md-8 col-lg-9 active-operation"
            v-if="activeContract && activeOperation"
        >
            <p class="retour" @click="showOperationList">
               <i class="fa fa-chevron-left"></i> Retour
            </p>
            <operation
                :contract="activeContract"
                :operation="activeOperation">
            </operation>
        </div>

        <div v-else class="flex">
            <div class="col-sm-5 col-md-4 col-lg-3">
                <contact-box v-for="contact in contacts" :contact="contact">
                </contact-box>
            </div>

            <div class="col-sm-7 col-md-8 col-lg-9">
                <template v-if="!loading && items.length">
                    <div v-for="operation of items"
                         :key="operation.id"
                         :contract="contract"
                         :operation="operation"
                         class="panel panel-default operation-box"
                    >
                        <a
                           class="panel-heading"
                           @click.prevent="showOperation(operation)"
                        >
                            <div>
                                <p>
                                    #{{ operation.reference_id }} - {{ {
                                        SUBSCRIPTION: 'SOUSCRIPTION',
                                        DEPOSIT: 'VERSEMENT LIBRE',
                                        WITHDRAWAL: 'RACHAT PARTIEL',
                                        PERIODICPAYMENT: 'VERSEMENT LIBRE PROGRAMMÉ',
                                        PERIODICPAYMENTSUSPENSION:
                                            'VERSEMENT LIBRE PROGRAMMÉ (ARRÊT)',
                                        RATTACHEMENT: "RATTACHEMENT CONTRAT",
                                    }[operation.type] }}
                                    <span v-if="isVlp(operation)">
                                        <span v-if="isVlpModification(operation)">
                                            (MODIFICATION)
                                        </span>
                                        <span v-else> (MISE EN PLACE)</span>
                                    </span>
                                </p>
                                <p>{{ date(operation.date_create, 'LL') }}</p>
                            </div>

                            <div>
                                <div
                                    class="label"
                                    :class="stateClass(operation.state)"
                                >
                                    {{ state(operation.state) }}
                                </div>
                            </div>
                        </a>
                    </div>
                </template>
            </div>
        </div>

        <template v-if="!loading && !items.length">
            <div class="g-ops-nothing">
                Aucune opération pour ce contrat
            </div>
        </template>
    </div>
</template>

<style lang="less">
@import "../../style/variables";
.active-operation {
    float: none;
    margin: 0 auto;
}

.operation-box {
    a {
        align-items: center;
        cursor: pointer;
        justify-content: space-between;
        display: flex;
    }

    p {
        margin-block: 0.5rem;
    }
}

.retour {
    color: @brand-primary;
    cursor: pointer;
}

.g-ops {
    &-loader {
        margin: 50px auto;
    }

    &-nothing {
        margin: 50px auto;
        text-align: center;
        opacity: 0.7;
    }
}
</style>

<script>
import {ObjectDebounce} from 'wasync';
import Loader from '../Loader';
import {manager as msgManager} from '../../messages';
import {listAll} from '../../api/operations';
import ContactBox from './ContactBox';
import Operation from './Operation.vue';

export default {
    props: {
        contract: {
            required: true
        },
        contacts: {
            required: true
        }
    },

    components: {
        Loader,
        ContactBox,
        Operation
    },

    data() {
        return {
            /**
             * Items as downloaded from the server
             */
            items: [],

            /**
             * Indicates that operations list is being loaded
             */
            loading: true,

            /**
             * When an item in the list is clicked, activeContract and
             * activeOperation are set and the detail of activeOperation is
             * shown
             */
            activeContract: null,
            activeOperation: null
        };
    },

    methods: {
        /**
         * Fetches the operations list
         */
        fetch: new ObjectDebounce({wait: 0}).func({
            validate() {
                return true;
            },

            prepare() {
                this.loading = true;
            },

            cleanup() {
                this.loading = false;
            },

            async run() {
                return await listAll({contract: this.contract.id});
            },

            success(items) {
                this.items = items;

                /**
                 * If an operation id is set in a query param, this operation
                 * is directly shown.
                 */
                const id = parseInt(this.$route.query.id, 10);
                if (id) {
                    const op = items.find(item => item.id === id);
                    if (op) this.showOperation(op);
                }
            },

            failure() {
                msgManager.push(
                    'Erreur',
                    'Impossible de charger les opérations',
                    'danger',
                );
            }
        }),

        /**
         * Formats a date. We could use filters but since the feature got
         * removed in future versions of Vue that would not be future proof
         * so let's start to behave like we're going to upgrade.
         */
        date(...args) {
            return this.$options.filters.date(...args);
        },

        /**
         * Given a state, returns the label of that state
         */
        state(state) {
            return {
                PENDING: 'À traiter',
                DONE: 'Fait',
                CANCELED: 'Annullée',
                ERROR: 'Erreur'
            }[state];
        },

        /**
         * Given a state, returns the class of that state
         */
        stateClass(state) {
            return {
                PENDING: 'label-warning',
                DONE: 'label-success',
                CANCELED: 'label-danger',
                ERROR: 'label-danger'
            }[state];
        },

        /**
         * If operation doesn't have 'documents' or 'requirements', documents
         * is set to 'DONE'.
         */
        validateOperation(operation) {
            if (!operation.documents && !operation.requirements) {
                operation.stages.documents = 'DONE';
            }

            return operation;
        },

        /**
         * Show the detail of the operation when clicking on a item in the list.
         */
        showOperation(operation) {
            const validatedOperation = this.validateOperation(operation);

            this.activeContract = this.contract;
            this.activeOperation = validatedOperation;
        },

        /**
         * Show operation list when clicking on the back link. When the operation
         * id is present in the URL as query param, updating the url is required
         * in order to remove this param when going back to the list.
         */
        showOperationList() {
            this.activeContract = null;
            this.activeOperation = null;
            const id = this.$route.params.id;
            const contractId = this.$route.params.contractId;
            window.history.pushState(
                '',
                '',
                `/clients/${id}/contracts/${contractId}?tab=operations`
            );
        },

        /**
         * Determines if operation is a periodic payment.
         */
        isVlp(operation) {
            if (operation.extra.request) {
                return operation.extra.request.type.toUpperCase() === 'PERIODICPAYMENT';
            }
            return false;
        },

        /**
         * For periodic payments, determines if it's a modification or not
         */
        isVlpModification(operation) {
            return (
                operation.extra.request.type.toUpperCase() === 'PERIODICPAYMENT' &&
                operation.extra.request.data.versement.is_modification
            );
        }
    },

    ready() {
        this.fetch();
    }
};
</script>



// WEBPACK FOOTER //
// OperationsTab.vue?2001885d